import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import cx from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateStampContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Stamp/EventCreateStampContext';
import {fabricGif} from '~/common/canvas/animation/gif/Gif';
import {selectStickers} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {EditorIcon} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/EditorIcons/EditorIcon';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';
import './Stickers.sass';

const ELEMENT_WIDTH = 200;

export const Stickers = ({flipEnvelope}) => {
  const [activeId, setActiveId] = useState(null);
  const {canvas} = useContext(EventCreateStampContext);
  const stickers = useSelector(selectStickers);
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(false);
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const isMobile = !isDesktop;
  useEffect(
    () => () => {
      if (canvas?.getActiveObject()) {
        canvas?.discardActiveObject()?.requestRenderAll();
      }
    },
    [canvas]
  );

  const onSelectionEvent = useCallback(
    (value) => {
      if (isMobile) {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {editingMobile: value},
        });
      }
    },
    [isMobile]
  );

  useEffect(() => {
    const handleSelectionCreatedOrUpdated = () => onSelectionEvent(true);

    canvas?.on('selection:created', handleSelectionCreatedOrUpdated);
    canvas?.on('selection:updated', handleSelectionCreatedOrUpdated);

    return () => {
      canvas?.off('selection:created', handleSelectionCreatedOrUpdated);
      canvas?.off('selection:updated', handleSelectionCreatedOrUpdated);
    };
  }, [canvas, onSelectionEvent]);

  const filterIconImage = (type) =>
    stickers?.reduce((previousValue, currentValue) => {
      const url =
        currentValue?.media?.find((media) => media?.label === type)?.url ||
        currentValue?.media?.find((media) => media?.label === 'image')?.url || // fallback if the icon doesn't exist
        currentValue?.media?.find((media) => media?.label === 'icon')?.url; // fallback if the image doesn't exist

      const sticker = {
        id: currentValue.id,
        url,
      };
      return [sticker, ...previousValue];
    }, []);

  const stickersIcons = filterIconImage('icon')?.reverse();
  const stickersImages = filterIconImage('image')?.reverse();

  const imagesMap = stickersImages?.reduce(
    (previousValue, currentValue) => ({
      ...previousValue,
      [currentValue.id]: currentValue.url,
    }),
    {}
  );

  const addElementToCanvas = (obj, isGif) => {
    obj.set({
      top: isDesktop ? obj.top + 50 : obj.top + 100,
      left: obj.left + 50,
    });

    canvas.add(obj);

    if (isGif) {
      window.fabric.util.requestAnimFrame(function render() {
        canvas.renderAll();
        window.fabric.util.requestAnimFrame(render);
      });
    }
    setActiveId(null);
  };

  const prepElementToAdd = async () => {
    setDisabled(true);
    const image = imagesMap[activeId];
    const isGif = image.split('.').pop() === 'gif';

    if (isGif) {
      const newGif = await fabricGif(image, {originalUrl: image}, ELEMENT_WIDTH);
      setDisabled(false);
      return addElementToCanvas(newGif, isGif);
    }

    window.fabric.Image.fromURL(image, (myImg) => {
      myImg.scaleToWidth(ELEMENT_WIDTH);
      addElementToCanvas(myImg, isGif);
      setDisabled(false);
    });
    return undefined;
  };

  useEffect(() => {
    if (activeId && canvas) {
      prepElementToAdd(canvas);
    }
  }, [activeId, canvas]);

  const onClickStickerHandler = useCallback(
    async (gif, id) => {
      flipEnvelope(true);
      setActiveId(id);
    },
    [flipEnvelope, setActiveId]
  );

  const iconsMemo = useMemo(
    () =>
      stickersIcons?.map((icon, idx) => (
        <EditorIcon
          key={icon.id}
          icon={icon.url}
          id={icon.id}
          onClick={onClickStickerHandler}
          hideAnimatedIndicator
          square
          height={40}
          width={40}
          disabled={disabled}
          loading={idx < 8 ? 'eager' : 'lazy'}
        />
      )),
    [stickersIcons]
  );

  return (
    <div
      className={cx('editor-icons', 'editor-icons__sticker', {
        'icons--mobile': isMobile,
      })}
    >
      {iconsMemo}
    </div>
  );
};
